import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { createInquiry } from "@/lib/api";

const INQUIRY_BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Inquiry", path: "/inquiry" },
];

const DESTS = [
  ["", "Any / Not sure yet"],
  ["dubai", "Dubai"],
  ["thailand", "Thailand"],
  ["singapore", "Singapore"],
  ["malaysia", "Malaysia"],
  ["vietnam", "Vietnam"],
  ["philippines", "Philippines"],
  ["bali", "Bali"],
];

export default function Inquiry() {
  const [params] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    inquiry_type: (params.get("type") || "LEISURE").toUpperCase(),
    destination: params.get("destination") || "",
    nights: params.get("nights") || "",
    pax: "",
    travel_date: "",
    budget: 250,
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const baseMessage = form.message.trim();
      const decoratedMessage = `${baseMessage}\n\n[Budget: ~$${form.budget}/pax]`;
      const payload = {
        ...form,
        nights: form.nights ? parseInt(form.nights, 10) : null,
        pax: form.pax ? parseInt(form.pax, 10) : null,
        destination: form.destination || null,
        message: decoratedMessage,
      };
      delete payload.budget;
      await createInquiry(payload);
      toast.success("Inquiry submitted — we'll respond within 24 hours.");
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="inquiry-success">
        <CheckCircle2 className="h-16 w-16 text-[#FF5238] mx-auto" />
        <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C] mt-8">
          Brief received.
        </h1>
        <p className="text-slate-600 mt-5 text-lg leading-relaxed">
          A dedicated consultant is reviewing your requirements and will reply within 24 hours.
          Meanwhile, try TrawellAI for an instant first estimate.
        </p>
        <div className="mt-10 flex justify-center gap-4">
          <Link to="/quote" className="btn-primary">Try AI First Quote <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/" className="btn-secondary">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32" data-testid="inquiry-page">
      <Seo
        path="/inquiry"
        title="Submit a Brief — DMC Proposal in 24 Hours"
        description="Tell us your destination, dates, pax, and budget. A dedicated Trawelldmc consultant returns a formal proposal within 24 hours."
        breadcrumb={INQUIRY_BREADCRUMB}
      />
      <div className="mb-14 max-w-2xl">
        <div className="overline mb-4">Inquiry</div>
        <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight text-[#0B192C]">
          Tell us your brief.
        </h1>
        <p className="text-slate-600 mt-5 text-lg leading-relaxed">
          The more you share, the sharper our proposal. We respond within 24 hours.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="inquiry-form">
        <FormField label="Full name *" required>
          <input
            required
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            data-testid="inquiry-name"
            className="inq-input"
          />
        </FormField>
        <FormField label="Email *" required>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            data-testid="inquiry-email"
            className="inq-input"
          />
        </FormField>
        <FormField label="Phone">
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            data-testid="inquiry-phone"
            className="inq-input"
          />
        </FormField>
        <FormField label="Company / Agency">
          <input
            type="text"
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
            data-testid="inquiry-company"
            className="inq-input"
          />
        </FormField>
        <FormField label="Inquiry type">
          <select
            value={form.inquiry_type}
            onChange={(e) => update("inquiry_type", e.target.value)}
            data-testid="inquiry-type"
            className="inq-input"
          >
            <option value="LEISURE">Leisure</option>
            <option value="MICE">MICE / Corporate</option>
            <option value="AGENT">Travel Agent Partnership</option>
            <option value="OTHER">Other</option>
          </select>
        </FormField>
        <FormField label="Destination">
          <select
            value={form.destination}
            onChange={(e) => update("destination", e.target.value)}
            data-testid="inquiry-destination"
            className="inq-input"
          >
            {DESTS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Nights">
          <input
            type="number"
            min="1"
            max="30"
            value={form.nights}
            onChange={(e) => update("nights", e.target.value)}
            data-testid="inquiry-nights"
            className="inq-input"
          />
        </FormField>
        <FormField label="Pax (headcount)">
          <input
            type="number"
            min="1"
            value={form.pax}
            onChange={(e) => update("pax", e.target.value)}
            data-testid="inquiry-pax"
            className="inq-input"
          />
        </FormField>
        <FormField label="Preferred travel date / month">
          <input
            type="date"
            value={form.travel_date}
            onChange={(e) => update("travel_date", e.target.value)}
            data-testid="inquiry-traveldate"
            className="inq-input"
          />
        </FormField>
        <FormField label={`Budget per pax: ~$${form.budget}`}>
          <input
            type="range"
            min="100"
            max="500"
            step="10"
            value={form.budget}
            onChange={(e) => update("budget", parseInt(e.target.value, 10))}
            data-testid="inquiry-budget"
            className="w-full accent-[#FF5238]"
          />
          <div className="flex justify-between text-[10px] text-slate-400 mt-1">
            <span>$100</span>
            <span>$300</span>
            <span>$500+</span>
          </div>
        </FormField>
        <FormField label="Trip style" full>
          <div className="flex bg-slate-100 p-1 w-full max-w-sm" role="tablist">
            {["LEISURE", "MICE"].map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={form.inquiry_type === t}
                onClick={() => update("inquiry_type", t)}
                data-testid={`inquiry-toggle-${t.toLowerCase()}`}
                className={`flex-1 py-2.5 px-4 text-xs font-heading font-semibold tracking-wider uppercase transition-colors ${
                  form.inquiry_type === t
                    ? "bg-[#0B192C] text-white"
                    : "text-slate-600 hover:text-[#0B192C]"
                }`}
              >
                {t === "LEISURE" ? "Leisure" : "MICE / Corporate"}
              </button>
            ))}
          </div>
        </FormField>
        <FormField label="Message *" full required>
          <textarea
            required
            rows={5}
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
            data-testid="inquiry-message"
            className="inq-input min-h-[120px] resize-vertical"
          />
        </FormField>

        <div className="md:col-span-2 flex flex-wrap items-center gap-4 pt-4">
          <button
            type="submit"
            disabled={submitting}
            data-testid="inquiry-submit"
            className="btn-primary disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Submit inquiry"} <ArrowRight className="h-4 w-4" />
          </button>
          <Link to="/quote" className="btn-ghost text-sm">Or get an AI quote first →</Link>
        </div>
      </form>

      <style>{`
        .inq-input {
          width: 100%;
          background: #fff;
          border: 1px solid #E2E8F0;
          padding: 0.875rem 1rem;
          font-size: 0.95rem;
          font-family: 'Manrope', sans-serif;
          color: #0B192C;
          outline: none;
          transition: border-color 0.2s ease;
        }
        .inq-input:focus { border-color: #FF5238; }
      `}</style>
    </div>
  );
}

function FormField({ label, children, full }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs font-semibold tracking-[0.12em] uppercase text-slate-600">{label}</span>
      {children}
    </label>
  );
}
